import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import {
  Activity,
  AppWindow,
  ArrowRight,
  BarChart3,
  Bell,
  Boxes,
  Building2,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  CircleUserRound,
  ClipboardCheck,
  Database,
  FileCheck2,
  FileSpreadsheet,
  Globe2,
  LayoutDashboard,
  LockKeyhole,
  Menu,
  PackageCheck,
  PanelTop,
  Search,
  ShieldCheck,
  Sparkles,
  Upload,
  Users,
  WandSparkles,
} from 'lucide-react';
import { demoDefinitions, isDemoId, type DemoId } from './demoDefinitions';

declare global {
  interface Window {
    __setDemoProgress?: (progress: number) => void;
    __demoReady?: boolean;
  }
}

type DemoSceneProps = {
  progress: number;
  portrait: boolean;
};

type StyleWithVars = CSSProperties & Record<`--${string}`, string | number>;

const clamp = (value: number, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const smooth = (value: number) => {
  const t = clamp(value);
  return t * t * (3 - 2 * t);
};

function phaseState(progress: number) {
  const scaled = clamp(progress, 0, 0.9999) * 5;
  const phase = Math.floor(scaled);
  return { phase, local: smooth(scaled - phase) };
}

function interpolate(points: readonly [number, number][], progress: number) {
  const scaled = clamp(progress) * (points.length - 1);
  const index = Math.min(points.length - 2, Math.floor(scaled));
  const local = smooth(scaled - index);
  const current = points[index];
  const next = points[index + 1];
  return [current[0] + (next[0] - current[0]) * local, current[1] + (next[1] - current[1]) * local] as const;
}

const revealAfter = (progress: number, start: number, duration = 0.08) => smooth((progress - start) / duration);

function StatusPill({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'good' | 'warn' }) {
  return <span className={`demo-status demo-status--${tone}`}>{children}</span>;
}

function DemoShell({
  children,
  title,
  eyebrow,
  progress,
  demoId,
  portrait,
  pointer,
}: {
  children: ReactNode;
  title: string;
  eyebrow: string;
  progress: number;
  demoId: DemoId;
  portrait: boolean;
  pointer: readonly [number, number];
}) {
  const definition = demoDefinitions[demoId];
  const { phase } = phaseState(progress);
  const pointerStyle = { '--pointer-x': pointer[0], '--pointer-y': pointer[1] } as StyleWithVars;
  return (
    <main className={`demo-canvas demo-canvas--${portrait ? 'portrait' : 'landscape'} demo-canvas--${demoId}`} data-phase={phase}>
      <div className="demo-app-frame">
        <header className="demo-app-header">
          <div className="demo-app-brand"><span>W</span><div><small>{eyebrow}</small><strong>{title}</strong></div></div>
          <div className="demo-header-tools"><Search aria-hidden="true" /><Bell aria-hidden="true" /><CircleUserRound aria-hidden="true" /></div>
        </header>
        {children}
        <footer className="demo-progress" aria-label="Demo workflow progress">
          <div className="demo-progress__track"><span style={{ width: `${clamp(progress) * 100}%` }} /></div>
          <div className="demo-progress__labels">
            {definition.steps.map((step, index) => <span className={index <= phase ? 'is-active' : ''} key={step}><i>{index + 1}</i>{step}</span>)}
          </div>
        </footer>
      </div>
      <div className="demo-fictional"><ShieldCheck aria-hidden="true" />Fictional product demonstration</div>
      <div className="demo-pointer" style={pointerStyle}><span /></div>
    </main>
  );
}

function AppNav({ active, portrait }: { active: string; portrait: boolean }) {
  const items = [[LayoutDashboard, 'Overview'], [Building2, 'Entities'], [AppWindow, 'Apps'], [Globe2, 'Websites'], [BarChart3, 'Reports']] as const;
  if (portrait) {
    return <nav className="demo-bottom-nav" aria-label="Demo navigation">{items.slice(0, 4).map(([Icon, label]) => <span className={label === active ? 'is-active' : ''} key={label}><Icon /><small>{label}</small></span>)}</nav>;
  }
  return <aside className="demo-side-nav"><div className="demo-workspace"><span>Workspace</span><strong>Portfolio view</strong></div>{items.map(([Icon, label]) => <span className={label === active ? 'is-active' : ''} key={label}><Icon /><small>{label}</small></span>)}<div className="demo-side-nav__access"><LockKeyhole /><small>Scoped access</small></div></aside>;
}

function PortfolioScene({ progress, portrait }: DemoSceneProps) {
  const { phase } = phaseState(progress);
  const pointer = interpolate(portrait ? [[86, 9], [52, 26], [72, 44], [82, 68], [61, 77]] : [[84, 10], [28, 31], [57, 45], [82, 69], [75, 76]], progress);
  const assistantVisible = revealAfter(progress, 0.58);
  const summaryVisible = revealAfter(progress, 0.76);
  const cards = [
    ['Apps', '4 connected', AppWindow],
    ['Websites', '3 monitored', Globe2],
    ['Snapshots', 'Updated today', Database],
  ] as const;
  return (
    <DemoShell title="Portfolio operations" eyebrow="Management platform" progress={progress} demoId="portfolio-operations" portrait={portrait} pointer={pointer}>
      <div className="demo-app-body">
        <AppNav active="Entities" portrait={portrait} />
        <section className="demo-main-panel">
          <div className="demo-page-heading"><div><small>Portfolio / Entities</small><h1>Operations overview</h1></div><button className={phase >= 1 ? 'is-selected' : ''}>All entities <ChevronDown /></button></div>
          <div className="demo-entity-rail">
            {['Workspace A', 'Workspace B', 'Workspace C'].map((label, index) => <button className={phase >= 1 && index === 1 ? 'is-active' : ''} key={label}><Building2 /><span><strong>{label}</strong><small>{index + 2} connected properties</small></span><ArrowRight /></button>)}
          </div>
          <div className="demo-metric-grid">
            {cards.map(([label, value, Icon], index) => <article className={phase >= 2 ? 'is-ready' : ''} style={{ '--delay': index } as StyleWithVars} key={label}><Icon /><span>{label}</span><strong>{value}</strong><small><CheckCircle2 /> Current</small></article>)}
          </div>
          <div className="demo-properties">
            <div className="demo-panel-title"><div><strong>Connected properties</strong><small>Workspace B</small></div><StatusPill tone="good">All current</StatusPill></div>
            <div className="demo-property-row"><AppWindow /><span><strong>Operations app</strong><small>Internal tool</small></span><StatusPill tone="good">Healthy</StatusPill></div>
            <div className="demo-property-row"><Globe2 /><span><strong>Customer website</strong><small>Public experience</small></span><StatusPill>Monitored</StatusPill></div>
          </div>
        </section>
        <aside className="demo-assistant" style={{ '--assistant-visible': assistantVisible } as StyleWithVars}>
          <div className="demo-assistant__title"><Sparkles /><div><strong>Portfolio assistant</strong><small>Read-only · permission aware</small></div></div>
          <div className="demo-assistant__prompt">Summarize what needs attention in Workspace B.</div>
          <div className="demo-assistant__thinking"><i /><i /><i /><span>Reviewing allowed sources</span></div>
          <div className="demo-assistant__answer" style={{ '--answer-visible': summaryVisible } as StyleWithVars}>
            <CheckCircle2 /><div><strong>No blocked systems.</strong><p>One inventory snapshot is due tomorrow. The website and operations app are current.</p><small>3 permitted sources checked</small></div>
          </div>
        </aside>
      </div>
    </DemoShell>
  );
}

function HealthScene({ progress, portrait }: DemoSceneProps) {
  const { phase } = phaseState(progress);
  const pointer = interpolate(portrait ? [[84, 7], [48, 30], [50, 55], [48, 69], [75, 77]] : [[85, 10], [39, 39], [44, 55], [45, 67], [72, 72]], progress);
  const complete = revealAfter(progress, 0.62);
  const nextVisible = revealAfter(progress, 0.78);
  const routines = [
    ['Morning check-in', '8:30 AM', Activity],
    ['Movement break', '12:15 PM', Bell],
    ['Evening reflection', '7:30 PM', ClipboardCheck],
  ] as const;
  const phone = (
    <section className="routine-app">
      <div className="routine-app__top"><span>9:41</span><div><span /><span /><span /></div></div>
      <header><div><small>Today</small><h1>Wednesday, August 12</h1></div><CalendarDays /></header>
      <div className="routine-progress"><span style={{ width: `${phase >= 3 ? 67 : 33}%` }} /><small>{phase >= 3 ? '2 of 3 complete' : '1 of 3 complete'}</small></div>
      <div className="routine-list">
        {routines.map(([title, time, Icon], index) => {
          const active = phase >= 1 && index === 1;
          const isComplete = index === 0 || (index === 1 && complete > 0.45);
          return <button className={`${active ? 'is-active' : ''} ${isComplete ? 'is-complete' : ''}`} key={title}><span className="routine-check">{isComplete && <Check />}</span><Icon /><span><strong>{title}</strong><small>{index === 1 && phase >= 2 ? 'Ready to log' : time}</small></span><ArrowRight /></button>;
        })}
      </div>
      <div className={`routine-log-sheet ${phase >= 2 ? 'is-open' : ''}`}>
        <div><small>Movement break</small><strong>{phase >= 3 ? 'Completed' : 'Log this check-in?'}</strong></div>
        <button className={phase >= 3 ? 'is-complete' : ''}>{phase >= 3 ? <><Check /> Logged at 12:18 PM</> : 'Mark complete'}</button>
      </div>
      <div className="routine-next" style={{ '--next-visible': nextVisible } as StyleWithVars}><Bell /><span><small>Up next</small><strong>Evening reflection · 7:30 PM</strong></span></div>
    </section>
  );
  return (
    <DemoShell title="Daily companion" eyebrow="Routine & wellness" progress={progress} demoId="routine-wellness" portrait={portrait} pointer={pointer}>
      <div className="health-demo-layout">
        {phone}
        {!portrait && <aside className="health-context"><div><small>Today’s plan</small><strong>One calm view for reminders, logging, and what comes next.</strong></div><div className="health-context__status"><CheckCircle2 /><span><strong>Schedule is current</strong><small>Changes sync to this profile only</small></span></div><div className="health-context__assistant"><Sparkles /><span><strong>Read-only assistant</strong><small>Can explain the schedule without changing it</small></span></div></aside>}
      </div>
    </DemoShell>
  );
}

function InventoryScene({ progress, portrait }: DemoSceneProps) {
  const { phase } = phaseState(progress);
  const pointer = interpolate(portrait ? [[84, 8], [50, 27], [48, 45], [67, 58], [72, 72]] : [[85, 10], [73, 19], [55, 45], [77, 54], [73, 70]], progress);
  const validation = revealAfter(progress, 0.36, 0.18);
  const actionVisible = revealAfter(progress, 0.72);
  const rows = [
    ['Item 1042', '12', '18 days', 'Current'],
    ['Item 2318', '4', '3 days', 'Low stock'],
    ['Item 4091', '22', '31 days', 'Current'],
  ] as const;
  return (
    <DemoShell title="Inventory & sales" eyebrow="Operations dashboard" progress={progress} demoId="inventory-sales" portrait={portrait} pointer={pointer}>
      <div className="demo-app-body">
        <aside className="demo-side-nav inventory-nav"><div className="demo-workspace"><span>Source</span><strong>Weekly export</strong></div>{[[LayoutDashboard, 'Overview'], [Upload, 'Import'], [Boxes, 'Inventory'], [BarChart3, 'Demand'], [ClipboardCheck, 'Data quality']].map(([Icon, label]) => <span className={(phase === 1 && label === 'Import') || (phase >= 2 && label === 'Inventory') ? 'is-active' : ''} key={label as string}><Icon /><small>{label as string}</small></span>)}</aside>
        <section className="demo-main-panel inventory-main">
          <div className="demo-page-heading"><div><small>Inventory / Current snapshot</small><h1>What needs attention</h1></div><button className={phase >= 1 ? 'is-selected' : ''}><Upload /> Import export</button></div>
          <div className="inventory-source"><FileSpreadsheet /><span><strong>weekly_inventory.xlsx</strong><small>Recurring source · 1,284 rows</small></span><StatusPill tone={phase >= 2 ? 'good' : 'neutral'}>{phase >= 2 ? 'Validated' : 'Ready'}</StatusPill></div>
          <div className="inventory-validation" style={{ '--validation': validation } as StyleWithVars}>
            <div><span>Reading source</span><Check /></div><div><span>Matching columns</span><Check /></div><div><span>Checking data quality</span><Check /></div>
          </div>
          <div className="inventory-table">
            <div className="inventory-table__head"><span>Item</span><span>Available</span><span>Coverage</span><span>Status</span></div>
            {rows.map(([item, available, coverage, status], index) => <div className={phase >= 3 && index === 1 ? 'is-alert' : ''} key={item}><span><Boxes />{item}</span><strong>{available}</strong><span>{coverage}</span><StatusPill tone={index === 1 ? 'warn' : 'good'}>{status}</StatusPill></div>)}
          </div>
          <div className="inventory-action" style={{ '--action-visible': actionVisible } as StyleWithVars}><PackageCheck /><span><small>Action prepared</small><strong>Create a replenishment review for Item 2318</strong></span><button>Open action <ArrowRight /></button></div>
        </section>
      </div>
    </DemoShell>
  );
}

function CommerceScene({ progress, portrait }: DemoSceneProps) {
  const { phase } = phaseState(progress);
  const pointer = interpolate(portrait ? [[84, 8], [35, 26], [65, 44], [49, 60], [67, 75]] : [[85, 10], [25, 35], [55, 44], [43, 62], [78, 67]], progress);
  const approved = revealAfter(progress, 0.48);
  const published = revealAfter(progress, 0.7);
  return (
    <DemoShell title="Commerce & brand" eyebrow="Connected operations" progress={progress} demoId="commerce-brand" portrait={portrait} pointer={pointer}>
      <div className="commerce-layout">
        <section className="commerce-ops">
          <div className="demo-page-heading"><div><small>Connected workflow</small><h1>Product update</h1></div><StatusPill tone={phase >= 4 ? 'good' : 'neutral'}>{phase >= 4 ? 'Live' : 'Draft'}</StatusPill></div>
          <div className="commerce-flow">
            <article className={phase >= 1 ? 'is-active' : ''}><Database /><span><small>1 · Feed</small><strong>Inventory snapshot</strong><em>1,284 records</em></span>{phase >= 1 && <Check />}</article>
            <i />
            <article className={phase >= 2 ? 'is-active' : ''}><FileCheck2 /><span><small>2 · Validate</small><strong>Content fields</strong><em>No blocking errors</em></span>{phase >= 2 && <Check />}</article>
            <i />
            <article className={phase >= 3 ? 'is-active' : ''}><WandSparkles /><span><small>3 · Approve</small><strong>Customer update</strong><em>Ready to publish</em></span>{phase >= 3 && <Check />}</article>
          </div>
          <div className="commerce-approval">
            <div><small>Proposed content update</small><strong>Collection availability refreshed from the validated feed.</strong><span>Only approved fields will be published.</span></div>
            <button className={approved > 0.5 ? 'is-approved' : ''}>{approved > 0.5 ? <><Check /> Approved</> : 'Approve update'}</button>
          </div>
          <div className="commerce-publish" style={{ '--published': published } as StyleWithVars}><Globe2 /><span><strong>Published to customer website</strong><small>Preview and production are in sync</small></span><CheckCircle2 /></div>
        </section>
        <section className="commerce-site-preview">
          <nav><strong>FIELD / GOODS</strong><span>Story</span><span>Catalog</span><Menu /></nav>
          <div className="commerce-site-hero"><small>Seasonal collection</small><h2>Useful goods,<br />ready when needed.</h2><p>Current availability from the connected operation.</p><button>View collection</button></div>
          <div className="commerce-site-cards"><div><span>01</span><strong>Everyday</strong></div><div><span>02</span><strong>Field</strong></div><div><span>03</span><strong>Studio</strong></div></div>
          <div className="commerce-site-live" style={{ '--published': published } as StyleWithVars}><Check /> Live content updated</div>
        </section>
      </div>
    </DemoShell>
  );
}

function ReportingScene({ progress, portrait }: DemoSceneProps) {
  const { phase } = phaseState(progress);
  const pointer = interpolate(portrait ? [[84, 8], [49, 22], [64, 42], [75, 61], [60, 74]] : [[85, 10], [28, 22], [63, 42], [82, 57], [73, 71]], progress);
  const accessVisible = revealAfter(progress, 0.6);
  const reportReady = revealAfter(progress, 0.78);
  const bars = [62, 78, 58, 85, 72, 92];
  return (
    <DemoShell title="Cross-company reporting" eyebrow="Shared reporting" progress={progress} demoId="cross-company-reporting" portrait={portrait} pointer={pointer}>
      <div className="demo-app-body">
        <AppNav active="Reports" portrait={portrait} />
        <section className="demo-main-panel reporting-main">
          <div className="demo-page-heading"><div><small>Reporting / Portfolio</small><h1>Aggregate overview</h1></div><button className={phase >= 1 ? 'is-selected' : ''}>All workspaces <ChevronDown /></button></div>
          <div className={`report-workspace-menu ${phase === 1 ? 'is-open' : ''}`}><button><Check /> All workspaces</button><button>Workspace A</button><button>Workspace B</button><button>Workspace C</button></div>
          <div className="report-kpis"><article><small>Current sources</small><strong>12</strong><span><CheckCircle2 /> All reporting</span></article><article><small>Open reviews</small><strong>3</strong><span>Across 2 workspaces</span></article><article><small>Data freshness</small><strong>Today</strong><span><CheckCircle2 /> Current</span></article></div>
          <div className="report-chart"><div className="demo-panel-title"><div><strong>Operational activity</strong><small>Portfolio comparison</small></div><StatusPill>Last 6 periods</StatusPill></div><div className="report-chart__plot">{bars.map((height, index) => <span style={{ '--bar-height': `${height}%`, '--bar-ready': phase >= 2 ? 1 : 0 } as StyleWithVars} key={index} />)}</div><div className="report-chart__legend"><span>Workspace A</span><span>Workspace B</span><span>Workspace C</span></div></div>
          <div className="report-ready" style={{ '--report-ready': reportReady } as StyleWithVars}><CheckCircle2 /><span><strong>Portfolio comparison ready</strong><small>12 permitted sources included</small></span></div>
        </section>
        <aside className="report-access" style={{ '--access-visible': accessVisible } as StyleWithVars}><div className="demo-assistant__title"><LockKeyhole /><div><strong>Access boundary</strong><small>Permission-aware reporting</small></div></div><div className="report-access__row"><Building2 /><span><strong>Workspace A</strong><small>Full reporting</small></span><Check /></div><div className="report-access__row"><Building2 /><span><strong>Workspace B</strong><small>Summary only</small></span><ShieldCheck /></div><div className="report-access__row is-locked"><Building2 /><span><strong>Workspace C</strong><small>Restricted details</small></span><LockKeyhole /></div><p>Aggregate values remain visible while restricted records stay hidden.</p></aside>
      </div>
    </DemoShell>
  );
}

const scenes: Record<DemoId, (props: DemoSceneProps) => ReactNode> = {
  'portfolio-operations': PortfolioScene,
  'routine-wellness': HealthScene,
  'inventory-sales': InventoryScene,
  'commerce-brand': CommerceScene,
  'cross-company-reporting': ReportingScene,
};

export function DemoCapture() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const requested = params.get('demo');
  const demoId = isDemoId(requested) ? requested : 'portfolio-operations';
  const portrait = params.get('format') === 'portrait';
  const initial = clamp(Number(params.get('progress') ?? 0));
  const [progress, setProgress] = useState(initial);
  const Scene = scenes[demoId];

  useEffect(() => {
    window.__setDemoProgress = (next) => setProgress(clamp(next));
    window.__demoReady = true;
    document.documentElement.dataset.demoCapture = 'true';
    return () => {
      delete window.__setDemoProgress;
      delete window.__demoReady;
      delete document.documentElement.dataset.demoCapture;
    };
  }, []);

  return <>{Scene({ progress, portrait })}</>;
}
